import React, { useRef } from "react";
import { Modal, ModalBody, Badge, Button } from "reactstrap";
import { Icon } from "../../components/Component";
import LogoDark from "../../images/new_logo.svg";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const STATUS_COLOR = {
  Created:   "warning",
  Confirmed: "info",
  Shipped:   "primary",
  Delivered: "success",
  Cancelled: "danger",
};

// ─── Styles (injected once) ───────────────────────────────────────────────────

const INVOICE_CSS = `
  .inv-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px;
    padding: 12px 16px;
    border-bottom: 1px solid #e9ecef;
    background: #f8f9fc;
  }
  .inv-toolbar-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
    font-size: 14px;
    flex-wrap: wrap;
  }
  .inv-toolbar-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }
  .inv-wrap {
    max-width: 760px;
    margin: 0 auto;
    padding: 28px 20px;
    background: #fff;
    overflow-x: hidden;
  }
  .inv-brand {
    text-align: center;
    margin-bottom: 24px;
  }
  .inv-brand img { height: 34px; }
  .inv-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: 28px;
    flex-wrap: wrap;
  }
  .inv-contact { flex: 1 1 180px; }
  .inv-meta    { flex: 1 1 160px; text-align: right; }
  .inv-meta-list {
    list-style: none;
    padding: 0;
    margin: 0;
    font-size: 13px;
  }
  .inv-meta-list li { margin-bottom: 4px; }
  .inv-contact-list {
    list-style: none;
    padding: 0;
    margin: 8px 0 0;
    font-size: 13px;
  }
  .inv-contact-list li {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    margin-bottom: 4px;
    word-break: break-word;
  }
  .inv-table-wrap {
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    margin-top: 4px;
    border-radius: 6px;
    border: 1px solid #e9ecef;
  }
  .inv-table {
    width: 100%;
    min-width: 480px;
    border-collapse: collapse;
  }
  .inv-table thead tr { background: #f5f6fa; }
  .inv-table th,
  .inv-table td {
    padding: 9px 12px;
    font-size: 12px;
    border-bottom: 1px solid #e9ecef;
    white-space: nowrap;
    text-align: left;
  }
  .inv-table td.inv-td-name { white-space: normal; word-break: break-word; }
  .inv-table tfoot tr:last-child td {
    border-top: 2px solid #364a63;
    font-size: 14px;
    font-weight: 700;
    color: #364a63;
    border-bottom: none;
  }
  .inv-footer {
    margin-top: 20px;
    padding-top: 14px;
    border-top: 1px solid #e9ecef;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    font-size: 12px;
    color: #8094ae;
  }
  .overline-title {
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #8094ae;
  }
  @media (max-width: 480px) {
    .inv-wrap { padding: 18px 12px; }
    .inv-meta  { text-align: left; }
    .inv-head  { flex-direction: column; gap: 12px; }
    .inv-toolbar { padding: 10px 12px; }
    .inv-toolbar-title { font-size: 13px; }
    .inv-footer { flex-direction: column; align-items: flex-start; }
  }
`;

const InjectStyles = () => (
  <style>{INVOICE_CSS}</style>
);

// ─── Print CSS (for window.print) ────────────────────────────────────────────

const PRINT_CSS = `
  * { box-sizing: border-box; }
  body { font-family: Arial, sans-serif; color: #000; background: #fff; margin: 0; padding: 0; }
  .inv-wrap { max-width: 720px; margin: 0 auto; padding: 36px 28px; }
  .inv-brand { text-align: center; margin-bottom: 24px; }
  .inv-brand img { height: 34px; }
  .inv-head { display: flex; justify-content: space-between; margin-bottom: 28px; flex-wrap: wrap; gap: 12px; }
  .inv-contact { flex: 1 1 180px; }
  .inv-meta { flex: 1 1 160px; text-align: right; }
  .inv-meta-list { list-style: none; padding: 0; margin: 0; font-size: 13px; }
  .inv-meta-list li { margin-bottom: 4px; }
  .inv-contact-list { list-style: none; padding: 0; margin: 8px 0 0; font-size: 13px; }
  .inv-contact-list li { margin-bottom: 3px; }
  h4.inv-name { font-size: 16px; margin: 6px 0; }
  h3.inv-title { font-size: 20px; font-weight: 700; margin: 0 0 8px; }
  .overline-title { font-size: 10px; text-transform: uppercase; letter-spacing: 0.08em; color: #8094ae; }
  .inv-table-wrap { width: 100%; overflow: visible; border: 1px solid #e9ecef; border-radius: 4px; }
  .inv-table { width: 100%; border-collapse: collapse; min-width: unset; }
  .inv-table thead tr { background: #f5f6fa; }
  .inv-table th, .inv-table td { padding: 8px 10px; font-size: 12px; border-bottom: 1px solid #e9ecef; text-align: left; }
  .inv-table tfoot tr:last-child td { border-top: 2px solid #364a63; font-size: 14px; font-weight: 700; color: #364a63; }
  .inv-notes { font-size: 11px; color: #8094ae; font-style: italic; margin-top: 12px; }
  .inv-footer { margin-top: 18px; padding-top: 12px; border-top: 1px solid #e9ecef; display: flex; justify-content: space-between; font-size: 12px; color: #8094ae; }
  .badge { display: inline-block; padding: 3px 8px; border-radius: 4px; font-size: 11px; font-weight: 600; }
  .badge-success { background: #d4edda; color: #155724; }
  .badge-warning { background: #fff3cd; color: #856404; }
  .badge-info    { background: #cce5ff; color: #004085; }
  .badge-primary { background: #d1ecf1; color: #0c5460; }
  .badge-danger  { background: #f8d7da; color: #721c24; }
`;

// ─── Component ────────────────────────────────────────────────────────────────

const OrderInvoiceModal = ({ isOpen, toggle, order }) => {
  const printRef = useRef();

  if (!order) return null;

  const subtotal   = order.products?.reduce((s, p) => s + p.qty * p.price, 0) || Number(order.total);
  const grandTotal = Number(order.total);

  // ── Print handler via hidden Iframe (No Page Reload) ────────────────────────
  const handlePrint = () => {
    const printContents = printRef.current.innerHTML;

    // Create a hidden iframe
    const iframe = document.createElement("iframe");
    iframe.style.position = "absolute";
    iframe.style.width = "0px";
    iframe.style.height = "0px";
    iframe.style.border = "none";
    iframe.style.visibility = "hidden";
    
    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(`
      <html>
        <head>
          <title>Invoice - ${order.order_ref}</title>
          <style>${PRINT_CSS}</style>
        </head>
        <body>
          ${printContents}
          <script>
            window.onload = function() {
              window.focus();
              window.print();
              setTimeout(function() {
                window.parent.document.body.removeChild(window.frameElement);
              }, 1000);
            };
          </script>
        </body>
      </html>
    `);
    iframeDoc.close();
  };

  // PDF download also opens the same print-to-PDF print dialog safely without reload
  const handleDownload = () => {
    handlePrint();
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      className="modal-dialog-centered"
      size="xl"
      scrollable
    >
      <InjectStyles />
      <ModalBody className="p-0">

        {/* ── Toolbar ────────────────────────────────────────────────── */}
        <div className="inv-toolbar">
          <div className="inv-toolbar-title">
            <Icon name="file-text" style={{ fontSize: 18, color: "#6576ff", flexShrink: 0 }} />
            <span>Invoice — #{order.order_ref}</span>
            <Badge color={STATUS_COLOR[order.order_status] || "secondary"}>
              {order.order_status}
            </Badge>
          </div>
          <div className="inv-toolbar-actions">
            <Button size="sm" color="primary" outline onClick={handlePrint}>
              <Icon name="printer-fill" className="me-1" />
              <span className="d-none d-sm-inline">Print / </span>PDF
            </Button>
            <button
              type="button"
              className="btn-close"
              onClick={toggle}
              aria-label="Close"
            />
          </div>
        </div>

        {/* ── Invoice Body ─────────────────────────────────────────────── */}
        <div
          ref={printRef}
          className="inv-wrap"
          style={{ overflowX: "hidden" }}
        >
          {/* Logo */}
          <div className="inv-brand">
            {/*
              new_logo.svg has an embedded PNG — on white background
              the logo's own background is also white/transparent so it looks fine.
              Use a colored pill behind it so it stands out on the white invoice.
            */}
            <div style={{
              display: "inline-block",
            }}>
              <img
                src={LogoDark}
                alt="ryzhm"
                style={{ height: 32, width: "auto", objectFit: "contain", display: "block" }}
              />
            </div>
          </div>

          {/* Head: Bill To + Invoice Meta */}
          <div className="inv-head">
            {/* Left — Customer */}
            <div className="inv-contact">
              <span className="overline-title">Invoice To</span>
              <h4
                className="inv-name"
                style={{ fontSize: 16, fontWeight: 700, margin: "6px 0 4px" }}
              >
                {order.customer?.name}
              </h4>
              <ul className="inv-contact-list">
                {order.customer?.address && (
                  <li>
                    <Icon name="map-pin-fill" style={{ color: "#6576ff", flexShrink: 0, marginTop: 1 }} />
                    <span style={{ wordBreak: "break-word" }}>{order.customer.address}</span>
                  </li>
                )}
                {order.customer?.phone && (
                  <li>
                    <Icon name="call-fill" style={{ color: "#6576ff", flexShrink: 0 }} />
                    <span>{order.customer.phone}</span>
                  </li>
                )}
                {order.customer?.email && (
                  <li>
                    <Icon name="mail" style={{ color: "#6576ff", flexShrink: 0 }} />
                    <span style={{ wordBreak: "break-all" }}>{order.customer.email}</span>
                  </li>
                )}
              </ul>
            </div>

            {/* Right — Invoice meta */}
            <div className="inv-meta">
              <h3
                className="inv-title"
                style={{ fontSize: 20, fontWeight: 700, marginBottom: 10 }}
              >
                Invoice
              </h3>
              <ul className="inv-meta-list">
                <li>
                  <span style={{ color: "#8094ae" }}>Invoice ID: </span>
                  <strong>#{order.order_ref}</strong>
                </li>
                <li>
                  <span style={{ color: "#8094ae" }}>Date: </span>
                  <span>{formatDate(order.created_at)}</span>
                </li>
                <li>
                  <span style={{ color: "#8094ae" }}>Payment: </span>
                  <span>{order.payment_method === "cod" ? "COD" : "Online"}</span>
                </li>
                <li style={{ marginTop: 8 }}>
                  <Badge color={order.payment_status === "paid" ? "success" : "warning"}>
                    {order.payment_status === "paid" ? "✓ Paid" : "Pending"}
                  </Badge>
                </li>
              </ul>
            </div>
          </div>

          {/* Items Table — scrollable on mobile */}
          <div className="inv-table-wrap">
            <table className="inv-table">
              <thead>
                <tr>
                  <th style={{ width: 30 }}>#</th>
                  <th>Product</th>
                  <th>Variant</th>
                  <th style={{ textAlign: "center", width: 45 }}>Qty</th>
                  <th style={{ textAlign: "right" }}>Price</th>
                  <th style={{ textAlign: "right" }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {order.products?.length > 0 ? (
                  order.products.map((prod, i) => (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      <td className="inv-td-name" style={{ whiteSpace: "normal", fontWeight: 500 }}>
                        {prod.name}
                      </td>
                      <td style={{ color: "#8094ae" }}>{prod.variant || "—"}</td>
                      <td style={{ textAlign: "center" }}>{prod.qty}</td>
                      <td style={{ textAlign: "right" }}>
                        ₹{Number(prod.price).toLocaleString("en-IN")}
                      </td>
                      <td style={{ textAlign: "right", fontWeight: 600 }}>
                        ₹{(prod.qty * prod.price).toLocaleString("en-IN")}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} style={{ textAlign: "center", color: "#8094ae", padding: 16 }}>
                      No product details available
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                {order.products?.length > 0 && (
                  <tr>
                    <td colSpan={4} style={{ border: "none" }}></td>
                    <td style={{ fontWeight: 600, fontSize: 12 }}>Subtotal</td>
                    <td style={{ textAlign: "right", fontWeight: 600, fontSize: 12 }}>
                      ₹{subtotal.toLocaleString("en-IN")}
                    </td>
                  </tr>
                )}
                <tr>
                  <td colSpan={4} style={{ border: "none" }}></td>
                  <td>Grand Total</td>
                  <td style={{ textAlign: "right", color: "#6576ff" }}>
                    ₹{grandTotal.toLocaleString("en-IN")}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Note */}
          <p
            className="inv-notes"
            style={{ fontSize: 11, color: "#8094ae", fontStyle: "italic", marginTop: 12 }}
          >
            This invoice was generated automatically and is valid without a signature or seal.
          </p>

          {/* Footer */}
          <div className="inv-footer">
            <span>ryzhm — Thank you for your order!</span>
            <span>
              Status:{" "}
              <strong style={{ color: "#364a63" }}>{order.order_status}</strong>
            </span>
          </div>
        </div>

      </ModalBody>
    </Modal>
  );
};

export default OrderInvoiceModal;
